import {
    Button,
    DateTimePickerInput,
    Form,
    Headline,
    InputControl,
    Loading,
    Picker,
    RequiredText,
    Spacer,
    StyleFunction,
    TextInput,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQFiskalyTaxPayerNatural,
    GQFiskalyTaxPayerType,
    useUpsertFiskalyTaxPayerNaturalMutation,
} from "graphql-sdk";
import { dateToDateTime, today } from "lib";
import { useCallback } from "react";
import React, { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { Divider } from "react-native-paper";
import { useTranslation } from "locales";
import {
    schemaTaxPayerNaturalForm,
    FiskalyTaxPayerNaturalFormType,
    defaultTaxPayerNaturalForm,
} from "../fiskaly-form";

export function NaturalTaxPayerWidget({
    taxPayer,
}: {
    taxPayer: GQFiskalyTaxPayerNatural;
}) {
    const [t] = useTranslation();
    const [upsertNaturalTaxPayer] = useUpsertFiskalyTaxPayerNaturalMutation();
    const { handleMutationError } = useHandleMutationError();
    const form = useForm(
        schemaTaxPayerNaturalForm,
        (taxPayer as FiskalyTaxPayerNaturalFormType) ||
            defaultTaxPayerNaturalForm
    );

    const [{ values }] = form;

    const save = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(async () => {
            await upsertNaturalTaxPayer({
                variables: {
                    taxPayerData: {
                        type: GQFiskalyTaxPayerType.Legal,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        salutation: values.salutation,
                        birthDate: values.birthDate,
                        identificationNumber: values.identificationNumber,
                        prefix: values.prefix,
                        suffix: values.suffix,
                        title: values.title,

                        webAddress: values.webAddress,

                        street: values.street,
                        houseNumber: values.houseNumber,
                        houseNumberSuffix: values.houseNumberSuffix,
                        zip: values.zip,
                        town: values.town,
                        country: "DE",

                        taxNumber: values.taxNumber,
                        taxOfficeNumber: values.taxOfficeNumber,
                        vatNumber: values.vatNumber,
                    },
                },
            });
        }, t("common.created", "Created"));
    }, [handleMutationError, t, upsertNaturalTaxPayer, values]);

    if (!values) {
        return <Loading />;
    }

    return <TaxPayerNaturalForm form={form} onSubmit={save} />;
}

function TaxPayerNaturalForm({
    form,
    onSubmit,
}: {
    form: Form<FiskalyTaxPayerNaturalFormType>;
    onSubmit: () => void;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    return (
        <>
            <Headline size="h2">
                {t(
                    "backoffice.common.general_information",
                    "General information"
                )}
            </Headline>
            <View style={styles.inputRow}>
                <InputControl
                    error={errors.salutation}
                    style={styles.salutation}
                >
                    <Picker
                        selectedValue={values.salutation}
                        style={styles.salutation}
                        label={t("backoffice.common.salutation", "Salutation")}
                        onValueChange={newValue =>
                            setValue("salutation", newValue)
                        }
                        required
                    >
                        <Picker.Item
                            key="salutation_1"
                            label={t("backoffice.common.salutation_herr", "Mr")}
                            value="1"
                        />
                        <Picker.Item
                            key="salutation_2"
                            label={t(
                                "backoffice.common.salutation_frau",
                                "Mrs"
                            )}
                            value="2"
                        />
                        <Picker.Item
                            key="salutation_3"
                            label={t(
                                "backoffice.common.salutation_divers",
                                "Diverse"
                            )}
                            value="3"
                        />
                    </Picker>
                </InputControl>
                <InputControl error={errors.title} style={styles.title}>
                    <TextInput
                        value={values.title}
                        label={t("backoffice.common.title", "Title")}
                        onChangeText={newValue => setValue("title", newValue)}
                        maxLength={30}
                    />
                </InputControl>
            </View>

            <View style={styles.inputRow}>
                <InputControl error={errors.prefix} style={styles.prefix}>
                    <TextInput
                        value={values.prefix}
                        label={t("backoffice.common.prefix", "Prefix")}
                        onChangeText={newValue => setValue("prefix", newValue)}
                        maxLength={30}
                    />
                </InputControl>

                <InputControl error={errors.firstName} style={styles.firstName}>
                    <TextInput
                        required
                        value={values.firstName}
                        label={t("backoffice.common.first_name", "First name")}
                        onChangeText={newValue =>
                            setValue("firstName", newValue)
                        }
                        maxLength={72}
                    />
                </InputControl>

                <InputControl error={errors.lastName} style={styles.lastName}>
                    <TextInput
                        required
                        value={values.lastName}
                        label={t("backoffice.common.last_name", "Last name")}
                        onChangeText={newValue =>
                            setValue("lastName", newValue)
                        }
                        maxLength={72}
                    />
                </InputControl>

                <InputControl error={errors.suffix} style={styles.suffix}>
                    <TextInput
                        value={values.suffix}
                        label={t("backoffice.common.suffix", "Suffix")}
                        onChangeText={newValue => setValue("suffix", newValue)}
                        maxLength={25}
                    />
                </InputControl>
            </View>

            <View style={styles.inputRow}>
                <InputControl error={errors.birthDate} style={styles.birthDate}>
                    <DateTimePickerInput
                        label={t("backoffice.common.birth_date", "Birth date")}
                        dateTimeType="date"
                        defaultValue={dateToDateTime(
                            new Date(values.birthDate)
                        )}
                        maxDate={today()}
                        onChangeDate={newDate => setValue("birthDate", newDate)}
                        required
                    />
                </InputControl>
                <InputControl
                    error={errors.identificationNumber}
                    style={styles.identificationNumber}
                >
                    <TextInput
                        label={t(
                            "backoffice.common.id_number",
                            "Identification number"
                        )}
                        value={values.identificationNumber}
                        onChangeText={newValue =>
                            setValue("identificationNumber", newValue)
                        }
                        maxLength={11}
                        required
                    />
                </InputControl>
            </View>

            <Divider />
            <Spacer />

            <Headline size="h2">
                {t("backoffice.common.address", "Address")}
            </Headline>
            <View style={styles.inputRow}>
                <InputControl error={errors.street} style={styles.street}>
                    <TextInput
                        required
                        value={values.street}
                        label={t("backoffice.common.street", "Street")}
                        onChangeText={newValue => setValue("street", newValue)}
                        maxLength={72}
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumber}
                    style={styles.houseNumber}
                >
                    <TextInput
                        required
                        value={values.houseNumber}
                        label={t(
                            "backoffice.common.house_number",
                            "House number"
                        )}
                        onChangeText={newValue =>
                            setValue("houseNumber", newValue)
                        }
                        maxLength={4}
                    />
                </InputControl>
                <InputControl
                    error={errors.houseNumberSuffix}
                    style={styles.houseNumberSuffix}
                >
                    <TextInput
                        label={t("backoffice.common.suffix", "Suffix")}
                        value={values.houseNumberSuffix}
                        onChangeText={newText =>
                            setValue("houseNumberSuffix", newText)
                        }
                        maxLength={20}
                    />
                </InputControl>
            </View>
            <View style={styles.inputRow}>
                <InputControl error={errors.zip} style={styles.zip}>
                    <TextInput
                        required
                        value={values.zip}
                        label={t("backoffice.common.zip", "Zip")}
                        onChangeText={newValue => setValue("zip", newValue)}
                        maxLength={5}
                    />
                </InputControl>
                <InputControl error={errors.town} style={styles.city}>
                    <TextInput
                        required
                        value={values.town}
                        label={t("backoffice.common.city", "City")}
                        onChangeText={newValue => setValue("town", newValue)}
                        maxLength={72}
                    />
                </InputControl>
            </View>

            <InputControl error={errors.webAddress}>
                <TextInput
                    value={values.webAddress}
                    label={t("backoffice.common.web_address", "Web address")}
                    onChangeText={newValue => setValue("webAddress", newValue)}
                    maxLength={1000}
                />
            </InputControl>

            <Divider />
            <Spacer />
            <Headline size="h2">
                {t(
                    "backoffice.integration.fiskaly.tax_information",
                    "Tax information"
                )}
            </Headline>
            <InputControl error={errors.taxNumber}>
                <TextInput
                    required
                    value={values.taxNumber}
                    label={t("backoffice.common.tax_number", "Tax number")}
                    onChangeText={newValue => setValue("taxNumber", newValue)}
                    maxLength={13}
                />
            </InputControl>
            <InputControl error={errors.taxOfficeNumber}>
                <TextInput
                    value={values.taxOfficeNumber}
                    label={t(
                        "backoffice.common.tax_office_number",
                        "Tax office number"
                    )}
                    onChangeText={newValue =>
                        setValue("taxOfficeNumber", newValue)
                    }
                    maxLength={4}
                />
            </InputControl>
            <InputControl error={errors.vatNumber}>
                <TextInput
                    value={values.vatNumber}
                    label={t("backoffice.common.vat_number", "VAT number")}
                    onChangeText={newValue => setValue("vatNumber", newValue)}
                    maxLength={11}
                />
            </InputControl>
            <Divider />
            <Spacer />
            <Button onPress={handleSubmit(onSubmit)} size="small">
                {t("common.save", "Save")}
            </Button>
            <RequiredText />
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    inputRow: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,
    },
    street: {
        ...theme.styles.flex3,
    },
    houseNumber: {
        ...theme.styles.flex2,
    },
    houseNumberSuffix: {
        ...theme.styles.flex1,
    },

    zip: {
        ...theme.styles.flex1,
    },
    city: {
        ...theme.styles.flex3,
    },

    salutation: {
        ...theme.styles.flex1,
    },
    title: {
        ...theme.styles.flex3,
    },
    prefix: {
        ...theme.styles.flex1,
    },
    suffix: {
        ...theme.styles.flex1,
    },
    firstName: { ...theme.styles.flex3 },
    lastName: { ...theme.styles.flex3 },

    birthDate: {
        ...theme.styles.flex1,
    },
    identificationNumber: {
        ...theme.styles.flex1,
    },
});
